import { Directive, inject, ElementRef, Renderer2, HostListener, OnInit, OnDestroy, forwardRef } from '@angular/core';
import { AbstractControl, ControlValueAccessor, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors, Validator } from '@angular/forms';
import { isEmailValid, normalizeMobile, normalizePhone, formatPhoneInput } from './contact-policy';

@Directive({
    selector: 'input[wbMobile], input[wbPhone], input[wbEmail]',
    standalone: false,
    providers: [
        { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => ContactInputDirective), multi: true },
        { provide: NG_VALIDATORS, useExisting: forwardRef(() => ContactInputDirective), multi: true }
    ]
})
export class ContactInputDirective implements ControlValueAccessor, Validator, OnInit, OnDestroy {
    private change: (value: string) => void = () => {};
    private touch: () => void = () => {};
    private validatorChanged: () => void = () => {};
    private reveal = false;
    private errorElement: HTMLElement | null = null;
    private static nextError = 0;
    private errorId = 'contact-input-error-' + (++ContactInputDirective.nextError);
    private element = inject(ElementRef<HTMLInputElement>);
    private renderer = inject(Renderer2);
    private get input(): HTMLInputElement { return this.element.nativeElement; }
    private get mobile(): boolean { return this.input.hasAttribute('wbMobile'); }
    private get email(): boolean { return this.input.hasAttribute('wbEmail'); }
    ngOnInit() {
        this.renderer.setAttribute(this.input, 'type', this.email ? 'email' : 'tel');
        this.renderer.setAttribute(this.input, 'inputmode', this.email ? 'email' : 'tel');
        if (this.mobile) this.renderer.setAttribute(this.input, 'maxlength', '13');
        else if (this.input.maxLength < 0) this.renderer.setAttribute(this.input, 'maxlength', this.email ? '254' : '20');
        if (this.mobile) this.renderer.setAttribute(this.input, 'placeholder', '010-0000-0000');
    }
    writeValue(value: unknown) {
        const raw = value == null ? '' : String(value);
        const formatted = this.email ? raw : (this.mobile ? normalizeMobile(raw) : normalizePhone(raw));
        this.renderer.setProperty(this.input, 'value', formatted == null ? raw : formatted);
    }
    registerOnChange(fn: (value: string) => void) { this.change = fn; }
    registerOnTouched(fn: () => void) { this.touch = fn; }
    registerOnValidatorChange(fn: () => void) { this.validatorChanged = fn; }
    setDisabledState(disabled: boolean) { this.renderer.setProperty(this.input, 'disabled', disabled); }
    private message(value: unknown): string {
        const current = value == null ? '' : String(value).trim();
        if (!current) return this.input.required ? '필수 항목을 입력해 주세요.' : '';
        if (this.input.maxLength >= 0 && current.length > this.input.maxLength) return '최대 ' + this.input.maxLength + '자까지 입력해 주세요.';
        if (this.email) return isEmailValid(current) ? '' : '올바른 이메일 형식으로 입력해 주세요. 예: member@example.com';
        if (this.mobile) return normalizeMobile(current) == null ? '휴대전화는 010-0000-0000 형식으로 입력해 주세요.' : '';
        return normalizePhone(current) == null ? '연락처 형식을 확인해 주세요. 예: 010-0000-0000 또는 02-0000-0000' : '';
    }
    validate(control: AbstractControl): ValidationErrors | null {
        const message = this.message(control.value);
        return message ? { contactFormat: { message } } : null;
    }
    private reflect() {
        const message = this.message(this.input.value);
        this.input.setCustomValidity(message);
        this.renderer.setAttribute(this.input, 'aria-invalid', this.reveal && message ? 'true' : 'false');
        if (this.reveal && message && !this.errorElement && this.input.parentNode) {
            const error = this.renderer.createElement('span') as HTMLElement;
            this.renderer.setAttribute(error, 'id', this.errorId);
            this.renderer.setAttribute(error, 'role', 'alert');
            this.renderer.setStyle(error, 'display', 'block');
            this.renderer.setStyle(error, 'margin-top', '0.35rem');
            this.renderer.setStyle(error, 'font-size', '0.75rem');
            this.renderer.setStyle(error, 'line-height', '1.5');
            this.renderer.setStyle(error, 'color', '#b42318');
            this.renderer.insertBefore(this.input.parentNode, error, this.input.nextSibling);
            this.errorElement = error;
            const described = this.input.getAttribute('aria-describedby') || '';
            this.renderer.setAttribute(this.input, 'aria-describedby', (described + ' ' + this.errorId).trim());
        }
        if (this.errorElement) {
            this.renderer.setProperty(this.errorElement, 'textContent', this.reveal ? message : '');
            this.renderer.setStyle(this.errorElement, 'display', this.reveal && message ? 'block' : 'none');
        }
    }
    @HostListener('input') onInput() {
        const raw = this.input.value;
        const caret = this.input.selectionStart;
        const before = caret == null ? 0 : (raw.slice(0, caret).match(/[0-9]/g) || []).length;
        const value = this.email ? raw : formatPhoneInput(raw, this.mobile);
        if (value !== raw) {
            this.renderer.setProperty(this.input, 'value', value);
            if (caret != null) {
                let position = 0, digits = 0;
                while (position < value.length && digits < before) { if (/[0-9]/.test(value[position])) digits++; position++; }
                try { this.input.setSelectionRange(position, position); } catch (_) {}
            }
        }
        this.change(value);
        this.validatorChanged();
        this.reflect();
    }
    @HostListener('blur') onBlur() {
        const raw = this.input.value;
        const value = this.email ? raw.trim() : (this.mobile ? normalizeMobile(raw) : normalizePhone(raw));
        if (value != null && value !== raw) { this.renderer.setProperty(this.input, 'value', value); this.change(value); }
        this.reveal = true;
        this.touch();
        this.validatorChanged();
        this.reflect();
    }
    ngOnDestroy() {
        if (this.errorElement && this.errorElement.parentNode) this.renderer.removeChild(this.errorElement.parentNode, this.errorElement);
        const described = (this.input.getAttribute('aria-describedby') || '').split(/\s+/).filter(id => id && id !== this.errorId).join(' ');
        if (described) this.renderer.setAttribute(this.input, 'aria-describedby', described);
        else this.renderer.removeAttribute(this.input, 'aria-describedby');
    }
}
